import { useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import TagManager from 'react-gtm-module'
import ScreenLoading from 'components/ScreenLoading'
import ProgressBar from 'components/ProgressBar'
import OnboardingGuide, { CopyUrlBlock } from 'components/Onboarding/OnboardingGuide'
import { MCP_SERVER_URL } from 'constants/mcp'
import { ETLProvider, useETLContext } from 'containers/etl/ETLContext'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import { selectToken } from 'containers/authentication/authenticationSlice'
import api from 'containers/api'
import { shouldReauthOnConfirm, markReauthDone, stashConfirmResume, clearConfirmResume } from 'utils/shopifyConfirmResume'

interface ShopifyConfirmProps {
  organizationSlug: string
}

interface SubscriptionInfo {
  id: string
  plan_name: string
  shop_domain: string
  price_amount: number
  discounted_price?: number
  discount_percentage?: number | null
  currency_code: string
}

const CenteredLoader: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '40px'
  }}>
    <ScreenLoading />
    <h3 style={{ marginTop: '24px', marginBottom: '12px' }}>{title}</h3>
    <p style={{ color: '#666', textAlign: 'center', maxWidth: '500px' }}>{subtitle}</p>
  </div>
)

// Wrapper: mount the shared ETLProvider (WebSocket live progress + trigger + cleanup) around
// the content so we reuse the existing ETL infrastructure instead of hand-rolling polling.
const ShopifyConfirm: React.FC<ShopifyConfirmProps> = ({ organizationSlug }) => {
  const { currentOrganization } = useContext(OrganizationsContext)

  if (!currentOrganization) {
    return <CenteredLoader title="Activating your subscription..." subtitle="This usually takes just a few seconds. Please don't close this page." />
  }

  return (
    <ETLProvider organization={currentOrganization} enableWebSocket={true}>
      <ShopifyConfirmContent organizationSlug={organizationSlug} />
    </ETLProvider>
  )
}

const ShopifyConfirmContent: React.FC<ShopifyConfirmProps> = ({ organizationSlug }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pollCount, setPollCount] = useState(0)
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null)
  const [etlComplete, setEtlComplete] = useState(false)
  const [syncError, setSyncError] = useState(false)
  const baselineRunsRef = useRef<number | null>(null)
  const kickedRef = useRef(false)
  const checkingRef = useRef(false)
  const attemptsRef = useRef(0)
  const runActiveRef = useRef(false)
  const sawActiveRef = useRef(false)
  const endedNoSuccessRef = useRef(0)
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollCancelledRef = useRef(false)
  const awaitingSubscriptionIdRef = useRef<string | null>(null)
  const confirmStartedRef = useRef(false)
  const history = useHistory()
  const location = useLocation()
  const { currentOrganization } = useContext(OrganizationsContext)
  const token = useSelector(selectToken)
  const { liveProgress, activeRunCount, triggerETL } = useETLContext()

  const queryParams = new URLSearchParams(location.search)
  const chargeId = queryParams.get('charge_id')
  // Store-switch resubscribe (embedded in the Shopify return URL by the subscribe endpoint):
  // the org's existing data belongs to the OLD store, so "already has data" must not count
  // as ready — completion requires the NEW store's first sync to actually finish.
  const switchedStore = queryParams.get('switched') === 'true'

  useEffect(() => {
    const confirmSubscription = async () => {
      try {
        // let the backend know they approved
        const confirmed = await api.confirmShopifySubscription({
          organizationId: currentOrganization.id,
          token,
          chargeId: chargeId || ''
        })
        // Approving a replacement plan: the org's previous subscription stays active until
        // the new one is swapped in, so wait for that specific subscription.
        awaitingSubscriptionIdRef.current = confirmed?.awaiting_subscription_id || null

        // poll until webhook activates the subscription
        pollSubscriptionStatus()
      } catch (err: any) {
        console.error('Failed to confirm subscription:', err)
        setError(err?.response?.data?.detail?.message || 'Failed to activate subscription')
        TagManager.dataLayer({ dataLayer: { event: 'checkout_failure_shopify_sub_activated', is_custom_track: true } })
        setLoading(false)
      }
    }

    // Run the confirm + poll exactly once (guarded by a ref), and depend on
    // currentOrganization.id — NOT the whole object, whose identity changes on any org-context
    // re-provide and would otherwise re-fire this billing-confirmation POST + restart the poll.
    if (!confirmStartedRef.current && currentOrganization.id && token) {
      confirmStartedRef.current = true
      pollCancelledRef.current = false
      confirmSubscription()
    }

    // Cancel the self-rescheduling poll on unmount so we don't setState on an
    // unmounted component or keep hitting the API in the background.
    return () => {
      pollCancelledRef.current = true
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization.id, token, chargeId])

  const pollSubscriptionStatus = async () => {
    if (pollCancelledRef.current) return
    try {
      const response = await api.getShopifySubscription({
        organizationId: currentOrganization.id,
        token
      })
      if (pollCancelledRef.current) return

      const awaitedId = awaitingSubscriptionIdRef.current
      if (response.success && response.subscription.status === 'active'
          && (!awaitedId || response.subscription.id === awaitedId)) {
        // A reactivation may be riding on DEAD stored tokens: nothing refreshes them
        // while the account sits inactive through a cancelled gap, and an uninstall or
        // a superseding OAuth kills them outright — after which every sync fails until
        // a manual reconnect (#1658). One instant re-auth hop guarantees fresh
        // credentials: the app is installed and the scopes unchanged, so Shopify
        // bounces straight back through /oauth/redirect (fresh grant + Reconnect
        // Required stamps cleared), Products consumes the stashed resume and routes
        // back here — the marker makes the hop once-per-charge, so no loop.
        if (response.subscription.shop_domain && shouldReauthOnConfirm(chargeId)) {
          markReauthDone(chargeId)
          try {
            stashConfirmResume(`${location.pathname}${location.search}`)
            const { authorization_url } = await api.startShopifyOAuth({
              shop: response.subscription.shop_domain,
              organizationId: currentOrganization.id,
              token,
            })
            if (authorization_url) {
              window.location.replace(authorization_url)
              return
            }
            // No URL came back — drop the stash so it can't hijack a later
            // legitimate /billing/products visit in this tab.
            clearConfirmResume()
          } catch (e) {
            // Never block activation on the hop — fall through to the normal flow
            // with the stored credentials (status quo behavior).
            clearConfirmResume()
            console.error('Shopify re-auth hop failed; continuing with stored credentials:', e)
          }
        }
        setSubscription(response.subscription)
        TagManager.dataLayer({ dataLayer: { event: 'checkout_success_shopify_sub_activated', is_custom_track: true } })
        setLoading(false)
      } else {
        // not yet, check if we should keep trying
        setPollCount(prevCount => {
          if (prevCount < 15) {
            pollTimeoutRef.current = setTimeout(() => pollSubscriptionStatus(), 2000)
            return prevCount + 1
          } else {
            setError('Subscription activation is taking longer than expected. Please refresh the page.')
            setLoading(false)
            return prevCount
          }
        })
      }
    } catch (err: any) {
      if (pollCancelledRef.current) return
      console.error('Failed to check subscription status:', err)

      // might not be created yet, keep trying
      if (err?.response?.status === 404) {
        setPollCount(prevCount => {
          if (prevCount < 15) {
            pollTimeoutRef.current = setTimeout(() => pollSubscriptionStatus(), 2000)
            return prevCount + 1
          } else {
            setError('Failed to check subscription status. Please refresh the page.')
            setLoading(false)
            return prevCount
          }
        })
      } else {
        setError('Failed to check subscription status. Please refresh the page.')
        setLoading(false)
      }
    }
  }

  // Keep the latest "is a run in flight" view in a ref so the poller below reads it without
  // having to re-create its interval (and without stale-closure bugs) on every progress tick.
  useEffect(() => {
    runActiveRef.current = !!liveProgress?.running || activeRunCount > 0
  }, [liveProgress?.running, activeRunCount])

  // NOTE: we deliberately do NOT treat a live WebSocket points>=100 reading as completion. On
  // connect the socket can replay a leftover 100 from a PREVIOUS finished run, which would flash
  // "ready" before this purchase's sync has run. Completion is confirmed only by the authoritative
  // total_runs baseline poll below (the progress bar is capped at 99% until then, so this costs at
  // most one ~3s poll of latency, never a false "ready").

  // Authoritative completion detection. Organization.etl_runs (total_runs) increments once per
  // finished run, so we poll it against a baseline captured on arrival. This is robust to the
  // run finishing before the WebSocket ever reports "running" (fast/empty first sync) — the case
  // the old points>=100 / everRan gating missed and left the page stuck on "syncing" forever.
  useEffect(() => {
    if (etlComplete || syncError || !subscription || !currentOrganization || !token) return
    let cancelled = false

    const RETURNING_GRACE = 4     // ~12s: give a just-activated run time to register as active.
    const SWITCH_KICK_AFTER = 10  // ~30s: on a store switch, self-kick ETL if nothing started.

    const evaluate = async () => {
      if (checkingRef.current) return // no overlapping in-flight requests
      checkingRef.current = true
      try {
        const settings = await api.getETLSettings({ organizationId: currentOrganization.id, token })
        if (cancelled) return
        const runs = typeof settings?.total_runs === 'number' ? settings.total_runs : 0
        if (baselineRunsRef.current === null) baselineRunsRef.current = runs
        const baseline = baselineRunsRef.current ?? runs
        attemptsRef.current += 1
        const runActive = runActiveRef.current
        if (runActive) sawActiveRef.current = true

        // A run finished since we arrived (covers both first-ever loads and a fresh sync kicked
        // for a returning/reactivating org) → data is ready.
        if (runs > baseline) {
          setEtlComplete(true)
          return
        }

        // First-ever load with nothing in flight → make sure a run is kicked. The server already
        // triggers on activation; this is a one-shot fallback and cleric de-duplicates per org.
        if (runs === 0 && !runActive && !kickedRef.current) {
          kickedRef.current = true
          try {
            await triggerETL()
            TagManager.dataLayer({ dataLayer: { event: 'shopify_onboarding_etl_triggered', is_custom_track: true } })
          } catch (e) {
            console.error('Failed to trigger first ETL:', e)
          }
          return
        }

        // Returning org that already has data and no fresh sync in flight → ready. Only if we've
        // never seen a run go active (a fresh sync would flip runActive/sawActive, and we then
        // wait for total_runs to increment instead), plus a grace window so a just-activated run
        // has time to register — otherwise we'd flash "ready" while a real sync is still running.
        // NEVER on a store switch: the existing data is the OLD store's, so declaring ready here
        // would present the wrong store's numbers as this purchase's data.
        if (!switchedStore && runs >= 1 && !runActive && !sawActiveRef.current && attemptsRef.current >= RETURNING_GRACE) {
          setEtlComplete(true)
          return
        }

        // Store switch with nothing seen running well past the grace window: the activation
        // webhook's ETL kick may have been lost (webhooks aren't redelivered once ack'd).
        // Kick one ourselves — cleric de-duplicates per org, so this is safe if the real one
        // is merely slow — and keep waiting for total_runs to increment past baseline.
        if (switchedStore && !runActive && !sawActiveRef.current && !kickedRef.current
            && attemptsRef.current >= SWITCH_KICK_AFTER) {
          kickedRef.current = true
          try {
            await triggerETL()
          } catch (e) {
            console.error('Failed to trigger post-switch ETL:', e)
          }
          return
        }

        // Failure recovery (no timer): a run we SAW go active has ended without total_runs
        // increasing — a failed run doesn't increment etl_runs, so this is a failed/skipped
        // sync, not a slow one (a slow sync keeps runActive true — the WS retains its last
        // running frame and the current-run poll still finds it). Require the "ended, not
        // completed" condition to hold for ~18s of consecutive polls before surfacing a
        // recoverable error, so a brief WS gap / reconnect between steps can't false-alarm on a
        // healthy slow sync. Any poll where the run looks active again resets the counter.
        // (The first-data-loaded email is success-gated, so this page is the only failure signal.)
        if (sawActiveRef.current && !runActive && runs <= baseline) {
          endedNoSuccessRef.current += 1
          if (endedNoSuccessRef.current >= 6) {
            setSyncError(true)
          }
          return
        }
        endedNoSuccessRef.current = 0
      } catch (e) {
        // ignore — keep polling
      } finally {
        checkingRef.current = false
      }
    }

    evaluate()
    const interval = setInterval(evaluate, 3000)
    return () => { cancelled = true; clearInterval(interval) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription, currentOrganization.id, token, etlComplete, syncError])

  const goToSources = () => {
    TagManager.dataLayer({ dataLayer: { event: 'clicked_connect_other_sources', is_custom_track: true } })
    history.push(`/organization/${organizationSlug}/data-insights/integrations`)
  }

  const handleRetrySync = async () => {
    // Re-arm the completion poller and kick a fresh sync. kickedRef included: it's almost
    // always true by the time a failure surfaced (the poller self-kicked), and both
    // self-kick fallbacks are gated on it — without resetting it, a retried run that dies
    // before ever registering active would leave the page spinning forever with every
    // exit (completion, self-kick, failure detection) gated shut.
    baselineRunsRef.current = null
    kickedRef.current = false
    sawActiveRef.current = false
    runActiveRef.current = false
    attemptsRef.current = 0
    endedNoSuccessRef.current = 0
    setSyncError(false)
    try {
      await triggerETL()
      TagManager.dataLayer({ dataLayer: { event: 'shopify_onboarding_etl_retried', is_custom_track: true } })
    } catch (e) {
      console.error('Failed to retry ETL:', e)
      setSyncError(true)
    }
  }

  if (loading) {
    return <CenteredLoader title="Activating your subscription..." subtitle="This usually takes just a few seconds. Please don't close this page." />
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '40px'
      }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          maxWidth: '500px'
        }}>
          <h3 style={{ marginTop: 0, color: '#c00' }}>Activation Error</h3>
          <p style={{ marginBottom: 0 }}>{error}</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    )
  }

  // Floor at 5% while syncing so a 0% state still reads as progress.
  const barPercent = etlComplete ? 100 : Math.max(5, Math.min(liveProgress?.points ?? 0, 99))

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '40px',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '48px',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        maxWidth: '560px',
        width: '100%',
        textAlign: 'center'
      }}>
        {/* Success icon */}
        <div style={{
          width: '72px',
          height: '72px',
          borderRadius: '50%',
          backgroundColor: '#e8f5e9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          <span className="material-icons-outlined" style={{ fontSize: '40px', color: '#4caf50' }}>
            check_circle
          </span>
        </div>

        <h2 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '1.5rem' }}>
          Thank you for your purchase!
        </h2>

        {subscription?.plan_name && (
          <p style={{ color: '#666', fontSize: '1.05rem', margin: '0 0 24px 0' }}>
            Your <strong>{subscription.plan_name}</strong> plan is now active.
          </p>
        )}

        {/* Subscription details */}
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '16px 20px',
          marginBottom: '24px',
          textAlign: 'left'
        }}>
          {subscription?.id && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#888', fontSize: '0.85rem' }}>Subscription ID</span>
              <span style={{ color: '#333', fontSize: '0.85rem', fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'right', maxWidth: '60%' }}>
                {subscription.id}
              </span>
            </div>
          )}
          {subscription?.shop_domain && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#888', fontSize: '0.85rem' }}>Store</span>
              <span style={{ color: '#333', fontSize: '0.85rem' }}>
                {subscription.shop_domain}
              </span>
            </div>
          )}
          {subscription?.price_amount !== undefined && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#888', fontSize: '0.85rem' }}>Amount</span>
              <span style={{ color: '#333', fontSize: '0.85rem' }}>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: subscription.currency_code || 'USD'
                }).format(
                  subscription.discount_percentage && subscription.discounted_price !== undefined
                    ? subscription.discounted_price
                    : subscription.price_amount
                )}
                {!!subscription.discount_percentage && ` (${subscription.discount_percentage}% off)`}
              </span>
            </div>
          )}
        </div>

        {syncError ? (
          /* ---------- Error state ---------- */
          <div style={{ textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span className="material-icons-outlined" style={{ fontSize: '20px', color: '#c0392b' }}>
                error_outline
              </span>
              <span style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                We hit a snag setting up your data
              </span>
            </div>
            <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 16px 0', lineHeight: '1.5' }}>
              Your first data sync didn’t complete. You can retry now, or come back later — once it
              succeeds we’ll email you and your insights will be ready. If it keeps failing, reach out to
              your Maya Insights account manager.
            </p>
            <button
              onClick={handleRetrySync}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 24px',
                backgroundColor: '#739570', color: 'white', border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontSize: '0.95rem', fontWeight: 500,
              }}
            >
              <span className="material-icons-outlined" style={{ fontSize: '18px' }}>refresh</span>
              Retry sync
            </button>
          </div>
        ) : !etlComplete ? (
          /* ---------- Syncing state ---------- */
          <div style={{ textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span className="material-icons-outlined" style={{ fontSize: '20px', color: '#739570' }}>
                sync
              </span>
              <span style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                Setting up your data…
              </span>
            </div>

            <ProgressBar loadValue={Math.round(barPercent)} maxValue={100} showProgress progressAlignment="right" />

            <p style={{ color: '#555', fontSize: '0.82rem', margin: '6px 0 16px 0' }}>
              {liveProgress?.description || (liveProgress?.running ? 'Syncing your Shopify data…' : 'Starting your first data sync…')}
            </p>

            <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 16px 0', lineHeight: '1.5' }}>
              This can take a little while for the first sync. <strong>You can close this page</strong> — we’ll email you
              the moment your data is ready, then you can start exploring your insights.
            </p>

            {/* MCP URL is available immediately — the user can connect their AI assistant now and
                it'll have data to work with as soon as the sync finishes. */}
            <div style={{ backgroundColor: '#e8f5e7', borderRadius: '8px', padding: '12px 16px', margin: '0 0 16px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#739570' }}>link</span>
                <span style={{ fontWeight: 600, color: '#739570', fontSize: '0.9rem' }}>Your MCP Server</span>
              </div>
              <p style={{ color: '#555', fontSize: '0.82rem', margin: '0 0 8px 0', lineHeight: 1.4 }}>
                Get a head start: copy this URL into Claude (or another AI assistant) now.
                You&apos;ll be able to authenticate once your first data sync completes and
                you receive the data-ready email.
              </p>
              <CopyUrlBlock
                url={MCP_SERVER_URL}
                onCopied={() => TagManager.dataLayer({ dataLayer: { event: 'clicked_copy_mcp_url', is_custom_track: true } })}
              />
            </div>

            <Link
              to={`/organization/${organizationSlug}/data-insights/ask-maya`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#739570', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}
            >
              Read the setup guide while you wait
              <span className="material-icons-outlined" style={{ fontSize: '18px' }}>arrow_forward</span>
            </Link>
          </div>
        ) : (
          /* ---------- Ready state ---------- */
          <div style={{ textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span className="material-icons-outlined" style={{ fontSize: '20px', color: '#4caf50' }}>
                celebration
              </span>
              <span style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                Your data is ready — start exploring!
              </span>
            </div>
            <OnboardingGuide variant="compact" organizationSlug={organizationSlug} />
          </div>
        )}

        {/* Action button — available in both states */}
        <div style={{ marginTop: '28px' }}>
          <button
            onClick={goToSources}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 32px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 500,
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
          >
            Connect other data sources
            <span className="material-icons-outlined" style={{ fontSize: '18px' }}>
              arrow_forward
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShopifyConfirm
